import type { InterpolationEngine } from "../engine/InterpolationEngine";
import type { CompactTimeline } from "../model/realtime/CompactTimeline";
import type { StopAnchorWire, TripDelayWire } from "../types/dtos/realtime-api/realtime.dto";

/** Epoch millisecond window derived from a compact timeline. */
export interface TimelineEpochTimeWindowMs {
    "start": number;
    "end":   number;
}

/**
 * Returns local midnight (00:00:00.000) for the given reference date.
 *
 * @param referenceDate Date used to determine the service day.
 * @returns Epoch milliseconds at local midnight.
 */
function localMidnightMs( referenceDate: Date ): number {
    const midnight = new Date( referenceDate );
    midnight.setHours( 0, 0, 0, 0 );

    return midnight.getTime();
}

/**
 * Converts a compact timeline's scheduled seconds to an epoch time window (ms).
 *
 * @param timeline Columnar trip timeline.
 * @param referenceDate Date used for midnight anchoring (defaults to now).
 * @returns Start/end epoch ms, or null when the timeline has no points.
 */
export function timelineEpochTimeWindowMs(
    timeline: CompactTimeline,
    referenceDate: Date = new Date(),
): TimelineEpochTimeWindowMs | null {
    if ( timeline.length === 0 ) {
        return null;
    }

    const midnightMs = localMidnightMs( referenceDate );

    return {
        "start": midnightMs + timeline.timestampS[0] * 1000,
        "end":   midnightMs + timeline.timestampS[timeline.length - 1] * 1000,
    };
}

/**
 * Builds a LineString coordinate ring from a compact timeline (no intermediate POJOs).
 *
 * @param timeline Columnar trip timeline.
 * @returns GeoJSON positions as [lon, lat] pairs.
 */
export function timelineToLineCoordinates( timeline: CompactTimeline ): [number, number][] {
    const coordinates: [number, number][] = [];
    for ( let i = 0; i < timeline.length; i += 1 ) {
        coordinates.push( [ timeline.lon[i], timeline.lat[i] ] );
    }

    return coordinates;
}

/**
 * Builds point features for debug map layers from a compact timeline.
 *
 * @param vehicleId Trip / vehicle identifier for feature properties.
 * @param vehicleLabel Display label for feature properties.
 * @param timeline Columnar trip timeline.
 * @returns GeoJSON Point features.
 */
export function timelineToPointFeatures(
    vehicleId: string,
    vehicleLabel: string,
    timeline: CompactTimeline,
): GeoJSON.Feature<GeoJSON.Point>[] {
    const features: GeoJSON.Feature<GeoJSON.Point>[] = [];

    for ( let i = 0; i < timeline.length; i += 1 ) {
        features.push( {
            "type":       "Feature",
            "properties": {
                vehicleId,
                "vehicleLabel":      vehicleLabel,
                "segmentIndex":      0,
                "shape_pt_sequence": i,
                "outBearing":        timeline.bearing[i],
            },
            "geometry": {
                "type":        "Point",
                "coordinates": [ timeline.lon[i], timeline.lat[i] ],
            },
        } );
    }

    return features;
}

/**
 * Calculates arrow polyline coordinates for a point and bearing (debug layers).
 *
 * @param longitude Longitude in degrees.
 * @param latitude Latitude in degrees.
 * @param bearing Heading in degrees.
 * @param size Arrow length in degree units.
 * @returns MultiLineString coordinate ring for one arrow.
 */
export function calculateTimelineArrowPoints(
    longitude: number,
    latitude: number,
    bearing: number = 0,
    size: number = 0.0001,
): [number, number][] {
    const bearingRad = ( bearing * Math.PI ) / 180;
    const basePoint: [number, number] = [ longitude, latitude ];
    const tipX = longitude + size * Math.sin( bearingRad );
    const tipY = latitude + size * Math.cos( bearingRad );
    const tipPoint: [number, number] = [ tipX, tipY ];
    const wingAngle1 = bearingRad + ( 135 * Math.PI ) / 180;
    const wingAngle2 = bearingRad - ( 135 * Math.PI ) / 180;
    const wingSize = size * 0.5;
    const wing1Point: [number, number] = [
        tipX + wingSize * Math.sin( wingAngle1 ),
        tipY + wingSize * Math.cos( wingAngle1 ),
    ];
    const wing2Point: [number, number] = [
        tipX + wingSize * Math.sin( wingAngle2 ),
        tipY + wingSize * Math.cos( wingAngle2 ),
    ];

    return [ basePoint, tipPoint, tipPoint, wing1Point, tipPoint, wing2Point ];
}

/**
 * Builds arrow features for debug map layers from a compact timeline.
 *
 * @param vehicleId Trip / vehicle identifier for feature properties.
 * @param timeline Columnar trip timeline.
 * @returns GeoJSON MultiLineString features (one per timeline point).
 */
export function timelineToArrowFeatures(
    vehicleId: string,
    timeline: CompactTimeline,
): GeoJSON.Feature<GeoJSON.MultiLineString>[] {
    const features: GeoJSON.Feature<GeoJSON.MultiLineString>[] = [];

    for ( let i = 0; i < timeline.length; i += 1 ) {
        const arrowPoints = calculateTimelineArrowPoints(
            timeline.lon[i],
            timeline.lat[i],
            timeline.bearing[i],
        );
        features.push( {
            "type":       "Feature",
            "properties": {
                vehicleId,
                "segmentIndex":      0,
                "pointIndex":        i,
                "shape_pt_sequence": i,
            },
            "geometry": {
                "type":        "MultiLineString",
                "coordinates": [ arrowPoints ],
            },
        } );
    }

    return features;
}

/**
 * Leading map-path coordinate when {@link InterpolationEngine.getPosition} returns null
 * (e.g. before departure) but a polyline head is still useful. Uses clamped scheduled time
 * without smoothing state — does not allocate {@link VehiclePosition} copies.
 *
 * @param params Timeline, delay, stops, timestamp, and shared interpolation engine.
 * @returns Latitude/longitude or null.
 */
export function getTimelineLeadingCoordAtMs( params: {
    "timeline":            CompactTimeline;
    "delay"?:              TripDelayWire;
    "stops":               StopAnchorWire[];
    "timestampMs":         number;
    "interpolationEngine": InterpolationEngine;
} ): {
    "lat": number;
    "lon": number;
} | null {
    const position = params.interpolationEngine.getPositionClampedForPath(
        params.timeline,
        params.delay,
        params.stops,
        params.timestampMs,
    );

    if ( position === null ) {
        return null;
    }

    return {
        "lat": position.lat,
        "lon": position.lon,
    };
}
